import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from '@angular/core';

export type StickyProgressBarCtaVariant =
  | 'main'
  | 'white'
  | 'black'
  | 'success';

/**
 * Shared bottom sticky bar: N-step breadcrumb + a free `<ng-content>` slot for
 * page-specific totals + a single CTA button.
 *
 * Purely presentational — reused, as-is, by seat-selection, checkout and summary:
 * it always stays fixed to the bottom of
 * the viewport in every view that renders it, on both desktop and mobile.
 *
 * GS-local component today: no cart-specific data lives here, so it has
 * no reason to depend on cart internals — it is a plausible future SDC candidate
 * if another app needs the same breadcrumb+CTA footer.
 */
@Component({
  selector: 'app-sticky-progress-bar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sticky-progress-bar.component.html',
  styleUrl: './sticky-progress-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StickyProgressBarComponent {
  /** 1-indexed step currently active. */
  currentStep = input.required<number>();
  steps = input<string[]>(['Select your Seats', 'Checkout']);

  ctaLabel = input.required<string>();
  ctaDisabled = input(false);
  ctaVariant = input<StickyProgressBarCtaVariant>('main');
  ctaTooltip = input<string>('');
  ctaDataCy = input<string>('sticky-progress-bar-cta');
  /**
   * Whether to render the lock icon next to the CTA label.
   * This bar is shared by seat-selection
   * ("Go to Checkout"), checkout ("Checkout" → submits payment) and summary
   * ("Back to Home") — a padlock only makes sense for the payment-submitting
   * state, so it's an explicit opt-in input rather than inferred from
   * `ctaLabel()` text, which would silently break if that copy ever changes.
   */
  ctaShowLockIcon = input(false);

  ctaClick = output<void>();

  /**
   * `main`'s disabled state is the neutral `btn-cta-disabled` gray (Figma footer): the
   * club color only shows once the CTA is enabled.
   *
   * `success`'s two classes (`btn-success`/`btn-checkout-gray`, styles.css) carry their own
   * solid fill (green/dark-gray) rather than leaning on the generic `disabled:opacity-30`
   * every other variant gets in the template below — a faded-green "disabled" state would
   * read as a dimmer green, not the distinct "not ready yet" dark gray the checkout CTA
   * needs (see checkout.component.ts's `canSubmitPayment`).
   */
  get ctaClasses(): string {
    switch (this.ctaVariant()) {
      case 'white':
        return 'btn-white';
      case 'black':
        return 'btn-black';
      case 'success':
        return this.ctaDisabled() ? 'btn-checkout-gray' : 'btn-success';
      default:
        // Disabled: neutral gray (Figma footer), never the club color.
        if (this.ctaDisabled()) {
          return 'btn-cta-disabled';
        }
        // `btn-main-light`, not `btn-main`: this bar is always `bg-black` (see the
        // template), and some tenants' own `default-color` (`.btn-main`'s bg) is
        // itself black/near-black — see styles.css's own comment on `.btn-main-light`.
        return 'btn-main-light';
    }
  }

  onCtaClick(): void {
    if (this.ctaDisabled()) {
      return;
    }
    this.ctaClick.emit();
  }
}
